import { FileObject } from '@highways/hooks/useUploadImage';
import { resizeFile } from '@utils';
import exifr from 'exifr';

export interface FileParts {
  uploadedImages: string[];
  uploadedImagesFileName: string[];
  uploadedImagesFileType: string[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
}

export type PartitionFileObjectsType = FileParts & {
  // defined as string so that it can be stored in localStorage
  geoDataArrays: {
    fileName: string | null;
    latitude: number | null;
    longitude: number | null;
  }[];
};

/**
 * This function uses exifr package to parse the image file to extract the latitude and longitude from exif data
 * @param file
 * @returns latitude & longitude of the image exif data
 */
export const getGeoDataFromImage = async (file: File) => {
  try {
    const { latitude, longitude } = await exifr.gps(file);
    if (!latitude || !longitude) {
      return { fileName: null, latitude: null, longitude: null };
    }
    return { fileName: file.name, latitude, longitude };
  } catch (error) {
    return { fileName: null, latitude: null, longitude: null };
  }
};

/**
 * This function partitions File objects into data URLs (base64 string), file names, file types and
 * blobs arrays so that they can be serialised and stored in localStorage
 * Regarding FileReader api, refer to: {@link https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL}
 * Regarding conversion from File object to base64 string for an array of File objects, refer to: {@link https://stackoverflow.com/questions/15960508/javascript-async-readasdataurl-multiple-files}
 * @param fileObjects the file objects uploaded by users
 */
export const partitionFileObjects = async (
  fileObjects: FileObject[],
): Promise<PartitionFileObjectsType> => {
  const dataURLs: string[] = [];
  const fileNames: string[] = [];
  const fileTypes: string[] = [];
  const blobs: { blob_uri: string; blob_endpoint: string; filename: string }[] =
    [];
  const geoDataArrays: {
    fileName: string | null;
    latitude: number | null;
    longitude: number | null;
  }[] = [];

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event.target?.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  for (const fileObject of fileObjects) {
    // Convert each File object to a data URL and then save the data URL, file name and file type
    const { file, blob_endpoint, blob_uri } = fileObject;
    try {
      const resizedFile = await resizeFile(file);
      const dataURL = await readFileAsDataURL(resizedFile);
      const geoData = await getGeoDataFromImage(file);
      dataURLs.push(dataURL);
      fileNames.push(file.name);
      fileTypes.push(file.type);
      if (geoData.latitude && geoData.longitude) {
        geoDataArrays.push({
          // slice the latitude and longitude to 8 decimal places to allow for localstorage
          fileName: file.name,
          latitude: Number(geoData.latitude),
          longitude: Number(geoData.longitude),
        });
      }
      if (blob_uri && blob_endpoint) {
        blobs.push({ blob_uri, blob_endpoint, filename: file.name });
      } else {
        blobs.push({ blob_uri: '', blob_endpoint: '', filename: file.name });
      }
    } catch (error) {
      throw new Error((error as Error).message);
    }
  }
  // this ensures all promises have been resolved before returning the data
  return {
    uploadedImages: dataURLs,
    uploadedImagesFileName: fileNames,
    uploadedImagesFileType: fileTypes,
    uploadedImagesBlob: blobs,
    geoDataArrays,
  };
};

/**
 * This function converts the data URLs (base64 string), filename, file-type and blobs back to File objects to be displayed in the UI
 * @param uploadedFileParts the data URLs, filename, file-type and blobs that have been uploaded
 * @returns
 */
export const convertFilePartsToFileObjects = async (
  uploadedFileParts: FileParts,
): Promise<FileObject[]> => {
  const {
    uploadedImages,
    uploadedImagesFileName,
    uploadedImagesFileType,
    uploadedImagesBlob,
  } = uploadedFileParts;
  if (!uploadedImages) return [];

  try {
    // Convert the data URLs back to File objects
    const filePromises = uploadedImages.map(async (dataURL, index) => {
      const blob = await fetch(dataURL as string).then((res) => res.blob());
      // return the file object with file name and file type
      const files = new File([blob], uploadedImagesFileName[index], {
        type: uploadedImagesFileType[index],
      });
      return {
        file: files,
        blob_uri: uploadedImagesBlob[index].blob_uri,
        blob_endpoint: uploadedImagesBlob[index].blob_endpoint,
      };
    });
    // Wait for all promises to resolve
    return await Promise.all(filePromises);
  } catch (error) {
    return [];
  }
};
